// @flow

import * as React from 'react';
import {useContext, useState} from 'react';
import {contextStubFunction} from 'docs/util';
import {usePrefs} from 'docs/data';
import type {GetPrefFunction} from 'docs/data';
import type {Condition, Conditions, Toggle, Toggles} from './Condition';

export type ConditionContextType = {
  toggles: Toggles,
  isVisible: (conditions: Conditions) => boolean,
  setToggleValue: (id: Toggle, value: boolean) => void,
};

export const ConditionContext = React.createContext<ConditionContextType>({
  toggles: {},
  isVisible: contextStubFunction('ConditionProvider'),
  setToggleValue: contextStubFunction('ConditionProvider'),
});

type ConditionProviderProps = {|
  children: React.Node,
|};

// These methods are exported and set up this way to make it easy to write useful unit tests
const check = (toggles: Toggles, getPref: GetPrefFunction) => (
  condition: Condition,
): boolean => {
  const resolved: Array<boolean> = [];
  if (condition.toggle) {
    resolved.push(toggles[condition.toggle]);
  }

  if (condition.server) {
    resolved.push(getPref('lang') === condition.server);
  }

  if (condition.client) {
    resolved.push(getPref('client') === condition.client);
  }

  if (condition.platform) {
    resolved.push(getPref('platform') === condition.platform);
  }

  if (condition.reader) {
    resolved.push(getPref('reader') === condition.reader);
  }

  return resolved.reduce((result, curr) => result && curr, true);
};

export const initIsVisible = (toggles: Toggles, getPref: GetPrefFunction) => ({
  when,
  unless,
}: Conditions): boolean => {
  const checkCondition = check(toggles, getPref);

  if ((when && !checkCondition(when)) || (unless && checkCondition(unless))) {
    return false;
  }

  return true;
};

export const ConditionProvider = (props: ConditionProviderProps) => {
  const [toggles, setToggleValues] = useState({});
  const {getPref} = usePrefs();

  const setToggleValue = (id: string, value: boolean) => {
    setToggleValues({...toggles, [id]: value});
  };

  const isVisible = React.useMemo(() => {
    return initIsVisible(toggles, getPref);
  }, [toggles, getPref]);

  const context = {
    toggles,
    setToggleValue,
    isVisible,
  };

  return (
    <ConditionContext.Provider value={context}>
      {props.children}
    </ConditionContext.Provider>
  );
};

export const useCondition = () => {
  return useContext(ConditionContext);
};
